// ============================================================================
// HAI Reporting Tool - Auto-save Hook
// Automatically saves form data to local storage with debouncing
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { db as localDb } from '@/services/db';
import { syncService } from '@/services/sync.service';

interface AutoSaveOptions {
  key: string; // Unique key for this form/data
  data: any; // Form data to save
  enabled?: boolean; // Enable/disable auto-save
  debounceMs?: number; // Debounce delay in milliseconds
  onSave?: (data: any) => void; // Callback when auto-save triggers
  onError?: (error: Error) => void; // Callback on error
}

interface AutoSaveState {
  isSaving: boolean;
  lastSavedAt?: Date;
  error?: string;
}

/**
 * Hook for auto-saving form data with debouncing
 * Saves to IndexedDB and queues for sync when online
 */
export function useAutoSave({
  key,
  data,
  enabled = true,
  debounceMs = 30000, // Default 30 seconds
  onSave,
  onError
}: AutoSaveOptions): AutoSaveState {
  const [state, setState] = useState<AutoSaveState>({
    isSaving: false
  });

  const timerRef = useRef<number | null>(null);
  const previousDataRef = useRef<any>(null);

  useEffect(() => {
    if (!enabled) return;

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Check if data has actually changed
    const dataString = JSON.stringify(data);
    const previousDataString = JSON.stringify(previousDataRef.current);

    if (dataString === previousDataString) {
      return; // No changes, skip
    }

    // Set new timer for debounced save
    timerRef.current = window.setTimeout(async () => {
      await performAutoSave();
    }, debounceMs);

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [data, enabled, debounceMs]);

  /**
   * Perform the actual auto-save
   */
  const performAutoSave = async (): Promise<void> => {
    setState(prev => ({ ...prev, isSaving: true, error: undefined }));

    try {
      // Save to IndexedDB with the key
      await saveToLocalStorage(key, data);

      // Update state
      previousDataRef.current = data;
      setState({
        isSaving: false,
        lastSavedAt: new Date()
      });

      // Call callback if provided
      onSave?.(data);

      console.log(`Auto-saved: ${key}`, data);
    } catch (error: any) {
      console.error('Auto-save error:', error);
      setState({
        isSaving: false,
        error: error.message
      });

      onError?.(error);
    }
  };

  /**
   * Save immediately (bypass debounce)
   */
  const saveNow = async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performAutoSave();
  };

  return {
    ...state,
    // Expose saveNow method if needed
    // @ts-ignore - adding extra property for convenience
    saveNow
  };
}

/**
 * Save data to local storage
 */
async function saveToLocalStorage(key: string, data: any): Promise<void> {
  // Store in a simple key-value table in IndexedDB
  // You could also use localStorage for simpler cases
  const storageKey = `autosave_${key}`;

  // Save to IndexedDB using appMetadata or a dedicated auto-save table
  await localDb.appMetadata.put({
    id: storageKey,
    ...data,
    _savedAt: new Date()
  } as any);
}

/**
 * Load auto-saved data
 */
export async function loadAutoSavedData(key: string): Promise<any | null> {
  try {
    const storageKey = `autosave_${key}`;
    const data = await localDb.appMetadata.get(storageKey);

    if (!data) return null;

    // Return data without metadata fields
    const { id, _savedAt, ...actualData } = data as any;
    return actualData;
  } catch (error) {
    console.error('Error loading auto-saved data:', error);
    return null;
  }
}

/**
 * Clear auto-saved data
 */
export async function clearAutoSavedData(key: string): Promise<void> {
  try {
    const storageKey = `autosave_${key}`;
    await localDb.appMetadata.delete(storageKey);
  } catch (error) {
    console.error('Error clearing auto-saved data:', error);
  }
}

/**
 * Hook to manage sync status display
 */
export function useSyncStatus() {
  const [status, setStatus] = useState<{
    isSyncing: boolean;
    pendingCount: number;
    lastSyncAt?: Date;
    error?: string;
  }>({
    isSyncing: false,
    pendingCount: 0
  });

  useEffect(() => {
    // Subscribe to sync status changes
    const unsubscribe = syncService.onSyncStatusChange(setStatus);

    // Load initial status
    syncService.getStatus().then(initialStatus => {
      setStatus(initialStatus);
    });

    return unsubscribe;
  }, []);

  return status;
}
